import { Modal } from 'antd';
import { t } from 'i18next';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Cropper, { type Area, type Point } from 'react-easy-crop';

interface ImageCropModalProps {
  isOpen: boolean;
  imageSrc: string;
  fileName: string;
  aspectRatio?: number;
  onClose: () => void;
  onCropComplete: (croppedFile: File, croppedDataUrl: string) => void;
}

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: Area,
  fileName: string
): Promise<{ file: File; dataUrl: string } | null> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return null;
  }

  canvas.width = Math.round(pixelCrop.width);
  canvas.height = Math.round(pixelCrop.height);

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    Math.round(pixelCrop.width),
    Math.round(pixelCrop.height)
  );

  const fileType = fileName.match(/\.png$/i) ? 'image/png' : 'image/jpeg';

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        const file = new File([blob], fileName.replace(/[^a-zA-Z0-9._-]/g, '_'), {
          type: fileType,
        });
        const dataUrl = canvas.toDataURL(fileType, 0.95);
        resolve({ file, dataUrl });
      },
      fileType,
      0.95
    );
  });
};

export const ImageCropModal: React.FC<ImageCropModalProps> = ({
  isOpen,
  imageSrc,
  fileName,
  aspectRatio,
  onClose,
  onCropComplete,
}) => {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const isDataUrl = !!imageSrc?.startsWith('data:') || !!imageSrc?.startsWith('blob:');
  const [fetchedSrc, setFetchedSrc] = useState<string>('');
  const [loadingSrc, setLoadingSrc] = useState<boolean>(!isDataUrl);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  const safeImageSrc = isDataUrl ? imageSrc : fetchedSrc;
  const isLoading = !isDataUrl && loadingSrc;

  useEffect(() => {
    if (isOpen) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !imageSrc || isDataUrl) return;

    let active = true;

    const convertToDataUrl = async () => {
      try {
        const response = await fetch(imageSrc);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          if (active && reader.result) {
            setFetchedSrc(reader.result as string);
            setLoadingSrc(false);
          }
        };
        reader.readAsDataURL(blob);
      } catch (err) {
        console.error(
          'Could not convert image URL to data URL via fetch, falling back to original URL:',
          err
        );
        if (active) {
          setFetchedSrc(imageSrc);
          setLoadingSrc(false);
        }
      }
    };

    convertToDataUrl();

    return () => {
      active = false;
    };
  }, [isOpen, imageSrc, isDataUrl]);

  const onCropCompleteCallback = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSave = async () => {
    if (!safeImageSrc || !croppedAreaPixels || isSavingRef.current || isLoading) return;

    isSavingRef.current = true;
    setIsSaving(true);

    try {
      const croppedResult = await getCroppedImg(safeImageSrc, croppedAreaPixels, fileName);
      if (croppedResult) {
        await onCropComplete(croppedResult.file, croppedResult.dataUrl);
      }
    } catch (err) {
      console.error('Failed to complete crop:', err);
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      title={t('crop_image', { defaultValue: 'Crop Image' })}
      onCancel={() => {
        if (!isSaving && !isSavingRef.current) onClose();
      }}
      footer={[
        <button
          key="cancel"
          type="button"
          className="btn btn-secondary btn-sm me-2"
          onClick={onClose}
          disabled={isSaving || isLoading}
        >
          {t('cancel')}
        </button>,
        <button
          key="save"
          type="button"
          className="btn btn-primary btn-sm"
          onClick={handleSave}
          disabled={isLoading || isSaving}
        >
          {isSaving ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              {t('saving', { defaultValue: 'Saving...' })}
            </>
          ) : (
            t('apply_crop', { defaultValue: 'Apply Crop' })
          )}
        </button>,
      ]}
      centered
      width={700}
      destroyOnClose
      zIndex={1060}
    >
      <div className="d-flex flex-column align-items-center justify-content-center my-3">
        {isLoading ? (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ minHeight: 350 }}
          >
            <div className="spinner-border text-primary my-4" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          safeImageSrc && (
            <>
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  height: 400,
                  backgroundColor: '#1a1a1a',
                  borderRadius: 6,
                  overflow: 'hidden',
                }}
              >
                <Cropper
                  image={safeImageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={aspectRatio}
                  onCropChange={setCrop}
                  onCropComplete={onCropCompleteCallback}
                  onZoomChange={setZoom}
                />
              </div>

              {/* Zoom Controls */}
              <div className="d-flex align-items-center justify-content-between gap-2 mt-3 w-100 px-2">
                <span className="text-muted small fw-medium">
                  {t('zoom', { defaultValue: 'Zoom' })}:
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center"
                  style={{ width: 28, height: 28, padding: 0 }}
                  onClick={() => setZoom((prev) => Math.max(1, Number((prev - 0.1).toFixed(2))))}
                  disabled={zoom <= 1 || isLoading || isSaving}
                  title={t('zoom_out', { defaultValue: 'Zoom Out' })}
                >
                  <span style={{ fontSize: 16, lineHeight: 1 }}>−</span>
                </button>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.05}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="form-range flex-grow-1 mb-0"
                  disabled={isLoading || isSaving}
                  style={{ cursor: 'pointer' }}
                />
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center"
                  style={{ width: 28, height: 28, padding: 0 }}
                  onClick={() => setZoom((prev) => Math.min(3, Number((prev + 0.1).toFixed(2))))}
                  disabled={zoom >= 3 || isLoading || isSaving}
                  title={t('zoom_in', { defaultValue: 'Zoom In' })}
                >
                  <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>
                </button>
                <span
                  className="text-muted small ms-1"
                  style={{ minWidth: 42, textAlign: 'right' }}
                >
                  {Math.round(zoom * 100)}%
                </span>
              </div>
            </>
          )
        )}
      </div>
    </Modal>
  );
};
